import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { userContactService } from '@/lib/api/user-contact.service';
import { UserContact } from '@/types/user-contact.types';
import { ImportContactsModal } from './ImportContactsModal';
import { useAuth } from '@/hooks/useAuth';

interface UserSearchSelectProps {
  onSelect: (contact: UserContact) => void;
  placeholder?: string;
  excludeIds?: number[];
  className?: string;
  showImportButton?: boolean;
  onContactImported?: () => void;
}

export const UserSearchSelect: React.FC<UserSearchSelectProps> = ({
  onSelect,
  placeholder,
  excludeIds = [],
  className = "",
  showImportButton = true,
  onContactImported
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [contacts, setContacts] = useState<UserContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search results when user changes (login/logout)
  useEffect(() => {
    setSearch('');
    setContacts([]);
    setIsOpen(false);
  }, [user?.id]);

  useEffect(() => {
    if (search.trim() && isOpen) {
      searchContacts(search);
    } else {
      setContacts([]);
    }
  }, [search, isOpen]);

  const searchContacts = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setContacts([]);
      return;
    }

    try {
      setLoading(true);
      
      const response = await userContactService.searchContacts({
        search: searchTerm,
        limit: 20
      });
      
      // Filter out already excluded contacts
      const filteredContacts = response.contacts.filter(
        (contact: any) => !excludeIds.includes(contact.id)
      );
      
      setContacts(filteredContacts);
    } catch (error) {
      console.error('Error searching contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (contact: UserContact) => {
    onSelect(contact);
    setSearch('');
    setIsOpen(false);
    setContacts([]);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (search.trim()) {
      searchContacts(search);
    }
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    onContactImported?.();
    // Refresh search if there's a query
    if (search.trim()) {
      searchContacts(search);
    }
  };

  const getContactDisplayName = (contact: UserContact) => {
    if (contact.contact_user) {
      return `${contact.contact_user.first_name} ${contact.contact_user.last_name}`;
    }
    return contact.full_name;
  };

  const getContactSubtitle = (contact: UserContact) => {
    const parts = [];
    if (contact.email) parts.push(contact.email);
    if (contact.relationship) parts.push(contact.relationship);
    if (contact.status === 'new') parts.push('(New Contact)');
    return parts.join(' • ');
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={placeholder ?? t('userSearch.placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={handleInputFocus}
            className="pl-10"
          />
        </div>
        {showImportButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            {t('actions.import')}
          </Button>
        )}
      </div>

      {isOpen && (
        <Card className="absolute z-50 w-full mt-1 max-h-80 overflow-auto shadow-lg">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : contacts.length > 0 ? (
              <div className="max-h-80 overflow-auto">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => handleSelect(contact)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center gap-3"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {getContactDisplayName(contact)}
                      </div>
                      {getContactSubtitle(contact) && (
                        <div className="text-sm text-gray-500 truncate">
                          {getContactSubtitle(contact)}
                        </div>
                      )}
                    </div>
                    {contact.status === 'new' && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {t('userSearch.newBadge')}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : search.trim() ? (
              <div className="p-4 text-center text-gray-500">
                <User className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <div className="text-sm">{t('userSearch.empty.noContacts')}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {t('userSearch.empty.tryDifferent')}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <div className="text-sm">{t('userSearch.empty.startTyping')}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showImportModal && (
        <ImportContactsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};
